import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface ReleaseState {
  shouldShow: boolean;
  version: string | null;
  changelogUrl: string | null;
  isLoading: boolean;
  acknowledge: () => Promise<boolean>;
}

export function useReleaseAnnouncement(): ReleaseState {
  const { user, API_BASE_URL, csrfToken, fetchCsrfToken, isCookieMode, currentBusiness, isBusinessSessionSynced } = useAuth();
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [lastSeenVersion, setLastSeenVersion] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [shouldShow, setShouldShow] = useState<boolean>(false);

  const changelogUrl = useMemo(() => {
    return currentVersion ? `/changelogs/v${currentVersion}.md` : null;
  }, [currentVersion]);

  useEffect(() => {
    let cancelled = false;

    // Only run when authenticated AND business session is established
    if (!user || !currentBusiness || !isBusinessSessionSynced) {
      setCurrentVersion(null);
      setLastSeenVersion(null);
      setShouldShow(false);
      return;
    }

    setIsLoading(true);
    (async () => {
      try {
        const vRes = await fetch(`${API_BASE_URL}/meta/version`, { credentials: 'include' });
        let v: string | null = null;
        if (vRes.ok) {
          const data = await vRes.json().catch(() => ({}));
          v = typeof data?.version === 'string' ? data.version : null;
        }
        if (cancelled) return;
        setCurrentVersion(v);

        // Only fetch last-seen when version is known
        if (v) {
          const seenRes = await fetch(`${API_BASE_URL}/me/release-last-seen`, { credentials: 'include' });
          if (cancelled) return;
          if (seenRes.ok) {
            const d2 = await seenRes.json().catch(() => ({}));
            const seen = typeof d2?.version === 'string' ? d2.version : null;
            setLastSeenVersion(seen);
            setShouldShow(Boolean(user) && v !== null && v !== seen);
          } else {
            // If we cannot read last-seen (e.g., 401 before business sync), do not show
            setLastSeenVersion(null);
            setShouldShow(false);
          }
        } else {
          setLastSeenVersion(null);
          setShouldShow(false);
        }
      } catch {
        if (!cancelled) {
          setShouldShow(false);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user, currentBusiness, isBusinessSessionSynced, API_BASE_URL]);

  const acknowledge = useCallback(async (): Promise<boolean> => {
    if (!currentVersion || !user || !currentBusiness || !isBusinessSessionSynced) return false;
    try {
      const csrf = isCookieMode ? (await fetchCsrfToken()) : null;
      const r = await fetch(`${API_BASE_URL}/me/release-last-seen`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
          ...(csrf && !csrfToken ? { 'X-CSRF-Token': csrf } : {}),
        },
        body: JSON.stringify({ version: currentVersion })
      });
      if (r.ok) {
        setLastSeenVersion(currentVersion);
        setShouldShow(false);
        return true;
      }
    } catch {}
    return false;
  }, [API_BASE_URL, currentVersion, csrfToken, fetchCsrfToken, isCookieMode, user, currentBusiness, isBusinessSessionSynced]);

  return { shouldShow, version: currentVersion, changelogUrl, isLoading, acknowledge };
} 